import { IconButton, Stack, styled, Tooltip } from '@mui/material'
import { SidebarLeftHideOMIcon, SidebarLeftShowOMIcon } from '@omnea-app/icons'
import { useSideNavContext } from './SideNavContext'
import type { ReactNode } from 'react'

type SideNavHeaderProps = {
  /** Shown while the nav is expanded, beside the toggle. */
  logo: ReactNode
  /** The logo on the rail's expand toggle, which shows the expand icon instead while hovered or focused. */
  railLogo: ReactNode
  expandLabel: string
  collapseLabel: string
  onToggleCollapsed: () => void
}

// Laid over the expand icon rather than wrapping it: the button sizes itself by its direct `svg` child.
const RailLogo = styled('span')(({ theme }) => {
  const fade = theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest })

  return {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: fade,
    '& + svg': { opacity: 0, transition: fade },
    'button:hover > &, button:focus-visible > &': { opacity: 0 },
    'button:hover > & + svg, button:focus-visible > & + svg': { opacity: 1 },
  }
})

/**
 * Figma's "Show Collapse" header for the `SideNav` header slot: the logo and a collapse toggle. The nav doesn't own
 * the toggle; the shell keeps the collapsed state and the toggle flips it.
 */
export const SideNavHeader = ({ logo, railLogo, expandLabel, collapseLabel, onToggleCollapsed }: SideNavHeaderProps) => {
  const { collapsed } = useSideNavContext()

  if (collapsed) {
    return (
      <Stack alignItems="center" sx={{ py: 3 }}>
        <Tooltip title={expandLabel} placement="right">
          <IconButton variant="ghost" color="secondary" aria-expanded={false} onClick={onToggleCollapsed}>
            <RailLogo>{railLogo}</RailLogo>
            <SidebarLeftShowOMIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 3, pl: 2 }}>
      {logo}
      <Tooltip title={collapseLabel} placement="right">
        <IconButton variant="ghost" color="secondary" aria-expanded onClick={onToggleCollapsed}>
          <SidebarLeftHideOMIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
